import HelpScrollArea from 'components/common/help-scroll-area';
import React, {useState, useEffect, useRef} from 'react';
import {io, Socket} from 'socket.io-client';
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import SHA256 from "crypto-js/sha256";

import ChatMessageItem from "./components/message";
import ChatAvatar from "./components/avatar";
import ChatInput from "./chat-input";

import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { alpha } from "@mui/material/styles";

import { WarningAmber } from "@vedmarket/ui-kit";
import {CHAT_API_URL, COLORS, PRODUCT_CHAT_SOCKET_URL, SOCKET_IO_ENABLED} from "../../../../../../../../../../../../../config/constants";
import {getToken, useUser} from "../../../../../../../../../../../../../api/auth";
import {ROLE_CHAT_MESSAGE} from "../../../../../../../../../../../../../utils/constants";
import {useGetCalcIdFromRoute} from "../../../../../../../../../../../../../utils/hooks";

import './chat.scss';

const getDefaultMessage = (t: TFunction): ChatMessage => ({
  role: ROLE_CHAT_MESSAGE.ASSISTANT,
  content: t('calc__text__packaging__chatDefaultMessage'),
})

function Chat({ handleApplyCode, isActive }: ChatProps) {

  const { t } = useTranslation();
  const defaultMessage = getDefaultMessage(t);

  const [ inputMessage, setInputMessage ] = useState('');
  const [ messages, setMessages ] = useState<ChatMessage[]>([ defaultMessage ]);
  const [ isConnected, setIsConnected ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);

  const user = useUser();
  const orderId = useGetCalcIdFromRoute();

  const socketRef = useRef<Socket | null>(null);
  const lastMessageRoleRef = useRef<string | null>(null);
  const currentAssistantRef = useRef('');
  const pendingUserMessageRef = useRef('');

  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  const answerStartedRef = useRef(false);
  const autoScrollRef = useRef(true);
  const scrollFrameRef = useRef<number | null>(null);
  const isActiveRef = useRef(isActive);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    isActiveRef.current = isActive;

    if (isActive) {
      autoScrollRef.current = true;
      scrollToBottom(true);
    }

    return () => {
      if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    };
  }, [ isActive ]);

  const scrollToBottom = (force = false) => {
    if (!force && !autoScrollRef.current) return;

    const container = chatScrollRef.current;

    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;

      if (!container || !isActiveRef.current || (!force && !autoScrollRef.current)) return;

      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'auto',
      });
    });
  };

  const handleUserScroll = (atBottom = false) => {
    autoScrollRef.current = atBottom;

    if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);

    const container = chatScrollRef.current;
    container?.scrollTo({ top: container.scrollTop, behavior: 'auto' });
  };

  const generateClientIdFromToken = (token: string) => {
    const hash = SHA256(token).toString();
    return hash.slice(0, 64);
  };

  useEffect(() => {
    if (!SOCKET_IO_ENABLED) return;

    const token = getToken();
    if (!token) return;

    const clientKey = (user.id + orderId).toString();
    const clientId = generateClientIdFromToken(clientKey);

    socketRef.current = io(PRODUCT_CHAT_SOCKET_URL, {
      path: '/ws/socket.io',
      query: { client_id: clientId },
      addTrailingSlash: false,
      transports: [ 'polling', 'websocket' ],
      closeOnBeforeunload: true,
      auth: { token },
    });

    const onConnect = () => {
      setIsConnected(true);

      fetch(`${CHAT_API_URL}/api/chat/${clientId}`)
        .then(res => res.json())
        .then(data => {
          if (data.messages) {
            setMessages(prev => [ ...prev, ...data.messages ]);
          }
        });
    };

    const onDisconnect = () => setIsConnected(false);

    socketRef.current.on('connect', onConnect);
    socketRef.current.on('disconnect', onDisconnect);

    socketRef.current.on('message', (data) => {
      if (data.type === ROLE_CHAT_MESSAGE.USER) {
        if (pendingUserMessageRef.current === data.content) {
          pendingUserMessageRef.current = '';

          if (isActiveRef.current) {
            setTimeout(() => scrollToBottom(true), 0);
          }

          return;
        }

        setMessages(prev => [
          ...prev,
          { role: ROLE_CHAT_MESSAGE.USER, content: data.content }
        ]);

        lastMessageRoleRef.current = ROLE_CHAT_MESSAGE.USER;

        if (isActiveRef.current) {
          setTimeout(() => scrollToBottom(true), 0);
        }

        return;
      }

      if (data.type === 'chunk') {
        if (!answerStartedRef.current) {
          answerStartedRef.current = true;
          setIsLoading(false);
        }

        const newText = currentAssistantRef.current + data.content;
        currentAssistantRef.current = newText;

        const boldMarkers = (newText.match(/\*\*/g) || []).length;
        const startsWithBold = newText.trimStart().startsWith("**");
        const isBalanced = boldMarkers % 2 === 0;

        const isFirstChunk =
              lastMessageRoleRef.current === ROLE_CHAT_MESSAGE.USER;

        if (isFirstChunk) {
          lastMessageRoleRef.current = ROLE_CHAT_MESSAGE.ASSISTANT;
        }

        const shouldRender = isBalanced || !startsWithBold;

        let displayText = newText;
        if (!startsWithBold && boldMarkers % 2 === 1) {
          displayText = newText.replace(/\*\*$/, '');
        }

        if (shouldRender) {
          setMessages(prevMessages => {
            const last = prevMessages[prevMessages.length - 1];

            if (last?.role === ROLE_CHAT_MESSAGE.ASSISTANT) {
              return [
                ...prevMessages.slice(0, -1),
                { ...last, content: displayText, alert: data.alert }
              ];
            }

            return [
              ...prevMessages,
              { role: ROLE_CHAT_MESSAGE.ASSISTANT, content: displayText, alert: data.alert }
            ];
          });

          if (isActiveRef.current) {
            setTimeout(() => scrollToBottom(false), 0);
          }
        }

        if (data.stop === true) {
          currentAssistantRef.current = '';
          answerStartedRef.current = false;
        }
      }
    });

    return () => {
      socketRef.current?.off('connect', onConnect);
      socketRef.current?.off('disconnect', onDisconnect);
      socketRef.current?.off('message');
      socketRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isActiveRef.current) scrollToBottom(false);
  }, [ messages.length ]);

  useEffect(() => {
    const scrollEl = chatScrollRef.current;
    if (!scrollEl) return;

    const handleWheel = (event: WheelEvent) => {
      const hasModifier = event.ctrlKey || event.shiftKey;
      const isVerticalWheel =
          Math.abs(event.deltaY) >= Math.abs(event.deltaX) && event.deltaY !== 0;
      const shouldIgnore = hasModifier || !isVerticalWheel;

      if (!isActiveRef.current || shouldIgnore) return;

      const atBottom =
          scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 5;
      const shouldFollow = event.deltaY > 0 && atBottom;

      handleUserScroll(shouldFollow);
    };

    scrollEl.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      scrollEl.removeEventListener('wheel', handleWheel);
    };
  }, []);


  const handleSendMessage = () => {

    if (!inputMessage.trim() || !isConnected || isLoading) return;

    const message = inputMessage;

    autoScrollRef.current = true;
    answerStartedRef.current = false;
    currentAssistantRef.current = '';
    pendingUserMessageRef.current = message;
    lastMessageRoleRef.current = ROLE_CHAT_MESSAGE.USER;
    setIsLoading(true);
    setMessages(prev => [
      ...prev,
      { role: ROLE_CHAT_MESSAGE.USER, content: message }
    ]);

    socketRef.current?.emit('chat_message', { content: message });
    setInputMessage('');

    setTimeout(() => scrollToBottom(true), 0);
  };

  const getMessage = (msg: ChatMessage) => {
    if (msg.alert) {
      return (
        <Alert
          severity='warning'
          icon={<WarningAmber width={22} height={22} style={{ color: COLORS.ORANGE, fill: COLORS.ORANGE }} />}
          sx={{
            borderRadius: 0,
            padding: '8px 16px',
            opacity: 0.8,
            '&.MuiAlert-standardWarning': {
              background: alpha(COLORS.ORANGE, 0.1),
            },
            color: COLORS.ORANGE,
            '& .MuiAlert-icon': {
              color: COLORS.ORANGE,
              opacity: 1,
              marginRight: '16px',
              padding: '8px 0'
            },
            '& .MuiAlert-message': {
              minWidth: 0,
              padding: '8px 0'
            }
          }}
        >
          <Typography variant='subtitle1' sx={{
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: 0,
            marginBottom: '4px',
            color: COLORS.WARNING_ALERT_TEXT
          }}>
            {t("common__message__warning")}
          </Typography>
          <Typography variant='body2' sx={{
            color: COLORS.WARNING_ALERT_TEXT,
            letterSpacing: 0,
            lineHeight: '21px'
          }}>
            {msg.content}
          </Typography>
        </Alert>
      );
    }

    return <ChatMessageItem msg={msg} handleApplyCode={handleApplyCode} />;
  };

  const handleClear = () => {
    setMessages([ defaultMessage ]);
    inputRef.current?.focus();
  }

  return (
    <div className={`chat${messages.length > 1 ? ' chat_populated' : ''}${messages.some(message => message.alert) ? ' chat_alert' : ''}`}>
      <Typography component='div' variant='caption' className='chat__header' sx={{
        color: COLORS.LIGHT_TEXT_SECONDARY,
        fontSize: '12px',
        lineHeight: '12px',
        letterSpacing: 0
      }}>
        {t("calc__text__packaging__chatSelectionHSCode")}
      </Typography>

      <HelpScrollArea
        className='chat__messages'
        contentClassName='chat__message-list'
        onUserScroll={handleUserScroll}
        label={t('calc__text__packaging__AIAssistant')}
        ref={chatScrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          const atBottom =
                  el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
          if (atBottom) {
            autoScrollRef.current = true;
          }
        }}
      >
        {messages.map((msg, i) => (
          <div key={i} className={`chat__entry${msg.alert ? ' chat__entry_alert' : ''}`}>{getMessage(msg)}</div>
        ))}

        {isLoading && (
          <div className='chat__assistant'>
            <div className='chat__message chat__message_assistant'>
              <ChatAvatar />
              <div className='chat__text chat__text_assistant'>
                <Typography component='span' variant='inherit' className='chat__dot'>.</Typography>
                <Typography component='span' variant='inherit' className='chat__dot'>.</Typography>
                <Typography component='span' variant='inherit' className='chat__dot'>.</Typography>
              </div>
            </div>
          </div>
        )}
      </HelpScrollArea>

      <ChatInput
        inputMessage={inputMessage}
        onChange={setInputMessage}
        onSend={handleSendMessage}
        onClear={handleClear}
        isConnected={isConnected}
        isLoading={isLoading}
        inputRef={inputRef}
      />
    </div>
  );
}

export default Chat;
